import { GooglePlaceInputValue } from "components/ValidatedFields/GooglePlaceInput";
import moment from "moment";
import React from "react";
import { getAppointmentPhase } from "utilities";
import { getTrainTravelClass } from "utilities/enumutils";

export interface TrainInfo {
  trainId: number
  artist: server.dto.ArtistSmall
  phase: server.dto.AppointmentPhase
  travellers: server.dto.ContactSmallV2[]

  bookingReference: string
  travelAgency: string
  notes: string
  trainNumber: string
  carriage: string
  seats: string
  travelClass: server.dto.TrainTravelClass
  files: server.dto.File[]

  departureLocal: moment.Moment
  departureStation: GooglePlaceInputValue
  departurePlatform: string

  arrivalLocal: moment.Moment
  arrivalStation: GooglePlaceInputValue
  arrivalPlatform: string
}

const mapTrainToState = (train: server.dto.Train, travellers: server.dto.ContactSmallV2[], files: server.dto.File[]): TrainInfo => ({
  trainId: train.trainId,
  artist: train.artist,
  phase: getAppointmentPhase(train.phase),
  travellers: travellers,

  bookingReference: train.bookingReference,
  travelAgency: train.travelAgency,
  notes: train.notes,
  trainNumber: train.trainNumber,
  carriage: train.carriage,
  seats: train.seats,
  travelClass: getTrainTravelClass(train.travelClass),
  files,

  departureLocal: moment.utc(train.dateTime.startDisplay),
  departureStation: {
    googlePlaceId: train.departure.googlePlaceId,
    placeIdentifier: train.departure.placeIdentifier,
    name: train.departure.name,
    address: train.departure.address.formatted.join(", "),
    geo: train.departure.latLng,
  },
  departurePlatform: train.departurePlatform,

  arrivalLocal: moment.utc(train.dateTime.endDisplay),
  arrivalStation: {
    googlePlaceId: train.arrival.googlePlaceId,
    placeIdentifier: train.arrival.placeIdentifier,
    name: train.arrival.name,
    address: train.arrival.address.formatted.join(", "),
    geo: train.arrival.latLng,
  },
  arrivalPlatform: train.arrivalPlatform,
});

export const useTrainDetail = (
  train: server.dto.Train,
  travellers: server.dto.ContactSmallV2[],
  files: server.dto.File[],
  onArtistChanged: (newArtist: server.dto.ArtistSmall) => void,
  onDateChanged: (newDate: server.dto.AppointmentDateTime) => void,
  onStatusChanged: () => void,
) => {
  const [state, setState] = React.useState<TrainInfo>(mapTrainToState(train, travellers, files));

  React.useEffect(() => {
    setState(mapTrainToState(train, travellers, files));
  }, [train, travellers, files]);

  const onValueChanged = (key: string, value: any) => {
    if (key === "artist") {
      setState({ ...state, artist: value });
    } else if (key === "travellers") {
      setState({ ...state, travellers: value });
    } else if (key === "status") {
      setState({ ...state, phase: value });
    } else if (key === "bookingReference") {
      setState({ ...state, bookingReference: value });
    } else if (key === "travelAgency") {
      setState({ ...state, travelAgency: value });
    } else if (key === "notes") {
      setState({ ...state, notes: value });
    } else if (key === "trainNumber") {
      setState({ ...state, trainNumber: value });
    } else if (key === "carriage") {
      setState({ ...state, carriage: value });
    } else if (key === "seats") {
      setState({ ...state, seats: value });
    } else if (key === "travelClass") {
      setState({ ...state, travelClass: value });
    } else if (key === "departureLocal") {
      const diff = value.diff(state.departureLocal, "minutes");
      const newArrivalLocal = state.arrivalLocal.clone().add(diff, "minutes");
      setState({
        ...state,
        departureLocal: value,
        arrivalLocal: newArrivalLocal,
      });
    } else if (key === "departureStation") {
      setState({ ...state, departureStation: value });
    } else if (key === "departurePlatform") {
      setState({ ...state, departurePlatform: value });
    } else if (key === "arrivalLocal") {
      setState({ ...state, arrivalLocal: value });
    } else if (key === "arrivalStation") {
      setState({ ...state, arrivalStation: value });
    } else if (key === "arrivalPlatform") {
      setState({ ...state, arrivalPlatform: value });
    }
  }

  const onSuccessfulStatusUpdate = () => {
    onStatusChanged();
  }

  const onSuccessfulDateUpdate = async (key: string, value: moment.Moment) => {
    const departureLocal = key === "departureLocal" ? value : state.departureLocal;
    let arrivalLocal = key === "arrivalLocal" ? value : state.arrivalLocal;

    if (key === "departureLocal") {
      const diff = value.diff(state.departureLocal, "minutes");
      arrivalLocal = state.arrivalLocal.clone().add(diff, "minutes");
    }

    onDateChanged({
      startDisplay: departureLocal.toDate(),
      endDisplay: arrivalLocal.toDate(),
      mode: "hasTime",
      customFallbackTime: "",
    });
  }

  const onSuccessfulArtistUpdate = async (newArtist: server.dto.ArtistSmall) => {
    const result = await window.CoreApi.CompanyToContact.ListContactByCompany({
      companyId: newArtist.artistId,
      defaultSelect: true,
      isActive: true,
      page: 1,
      perPage: 1000,
    });

    if (result.data.hasErrors) {
      return;
    }

    const newTravellers = result.data.data.map(c => ({
      contactId: c.contact.contactId,
      culture: c.contact.culture,
      gender: c.contact.gender,
      isValidated: c.contact.isValidated,
      name: c.contact.name,
      position: c.contact.position,
      profileImageUrl: c.contact.profileImageUrl,
    }))

    const updateDetailsResults = await window.CoreApi.Train.UpdateTrain({
      ...getUpdateTrainDetails({
        ...state,
        artist: newArtist,
        travellers: newTravellers,
      }),
    });

    if (updateDetailsResults.data.hasErrors) {
      return;
    }

    setState({
      ...state,
      artist: newArtist,
      travellers: newTravellers,
    });

    onArtistChanged(newArtist);
  }

  const onSuccessfulAddressUpdate = async () => {
    const newData = await window.CoreApi.Train.GetSidePanelData(train.trainId);
    if (newData.data.hasErrors) {
      return;
    }

    setState({
      ...state,
      departureStation: {
        googlePlaceId: newData.data.data.train.departure.googlePlaceId,
        placeIdentifier: newData.data.data.train.departure.placeIdentifier,
        name: newData.data.data.train.departure.name,
        address: newData.data.data.train.departure.address.formatted.join(", "),
        geo: newData.data.data.train.departure.latLng,
      },
      arrivalStation: {
        googlePlaceId: newData.data.data.train.arrival.googlePlaceId,
        placeIdentifier: newData.data.data.train.arrival.placeIdentifier,
        name: newData.data.data.train.arrival.name,
        address: newData.data.data.train.arrival.address.formatted.join(", "),
        geo: newData.data.data.train.arrival.latLng,
      },
    });
  }

  const refetchFiles = async () => {
    const response = await window.CoreApi.Train.GetSidePanelData(train.trainId);

    if (response.data.hasErrors) {
      return;
    }

    setState(prev => ({ ...prev, files: response.data.data.files }));
  }

  const onSuccessfulUpdate = async (key: string, newValue: any) => {
    if (key === "status") {
      onSuccessfulStatusUpdate();
      return;
    }

    if (key === "artist") {
      await onSuccessfulArtistUpdate(newValue);
      return;
    }

    if (key === "arrivalLocal" || key === "departureLocal") {
      await onSuccessfulDateUpdate(key, newValue);
      return;
    }

    if (key === "departureStation" || key === "arrivalStation") {
      await onSuccessfulAddressUpdate();
      return;
    }
  }

  const getUpdateTrainDetails = (trainInfo: TrainInfo): server.dto.UpdateTrainInstruction => ({
    arrivalGooglePlaceId: trainInfo.arrivalStation.googlePlaceId,
    arrivalLocal: trainInfo.arrivalLocal.toDate(),
    arrivalPlatform: trainInfo.arrivalPlatform,
    artistId: trainInfo.artist.artistId,
    bookingReference: trainInfo.bookingReference,
    carriage: trainInfo.carriage,
    departureGooglePlaceId: trainInfo.departureStation.googlePlaceId,
    departureLocal: trainInfo.departureLocal.toDate(),
    departurePlatform: trainInfo.departurePlatform,
    notes: trainInfo.notes,
    passengers: trainInfo.travellers.map(t => t.contactId),
    phase: trainInfo.phase.value,
    seats: trainInfo.seats,
    trainId: train.trainId,
    trainNumber: trainInfo.trainNumber,
    travelAgency: trainInfo.travelAgency,
    travelClass: trainInfo.travelClass?.value ?? "unknown",
  });

  return {
    state,
    onValueChanged,
    onSuccessfulUpdate,
    refetchFiles,
    getUpdateTrainDetails,
  }
};